import { AxiosRequestService } from '@/enums/api'
import { useServiceRequest } from '@/lib/api'
import CONFIG from '@/lib/config'
import { DocumentNode } from '@apollo/client'
import {
  Lease,
  MoveOutInputType,
  MoveOut,
  MoveOutStatus,
  GetLeaseCashLedgerResponse,
  GetLeaseBalanceResponse,
  GetPrepaymentResponse,
  LeaseSetting,
} from '@/types/lease'
import { useAuth0 } from '@auth0/auth0-react'
import get from 'lodash/get'
import {
  getActiveLeaseQuery,
  getLeaseQuery,
  getLeaseCashLedgerQuery,
  getLeaseOccupantsQuery,
  getAnimalPersonMapsQuery,
  getLeaseBalanceQuery,
  getPrepaymentQuery,
  getActiveLeaseSettingByLeaseIdQuery,
} from '../graphql/queries/lease'
import { getAddressByDoorIdQuery } from '../graphql/queries/address'
import {
  getMoveOutQuery,
  getMoveOutReasonsQuery,
  getMoveOutStatusesQuery,
} from '../graphql/queries/moveOut'
import { getAddressByPersonIdQuery } from '../graphql/queries/address'
import { createMoveOutQuery } from '../graphql/mutations/moveOut'
import { Address } from '../types/address'
import {
  Resident,
  TermsAndConditionsAcceptance,
  TermsOfServiceVersion,
  PrivacyPolicyVersion,
  AcceptTermsAndConditionsInput,
  AcceptTermsAndConditionsResponse,
  GetManagementFeePoliciesResponse,
} from '@/types/resident'
import {
  getResidentQuery,
  getTermsAndConditionsAcceptanceQuery,
  getActiveTermsOfServiceVersionQuery,
  getActivePrivacyPolicyVersionQuery,
  getManagementFeePoliciesQuery,
} from '@/graphql/queries/resident'
import { upsertForwardingAddressMutation } from '@/graphql/mutations/resident'
import { acceptTermsAndConditionsMutation } from '@/graphql/mutations/resident'
import {
  MoveOutReason,
  Occupant,
  AnimalPersonMap,
  PersonArray,
} from '../types/lease'
import { MoveOutStatusId, AddressTypeId } from '@purepm/purepm-lovs'
import { concatenateAddress } from '@/utils/address'
import {
  GetRecurrentChargesByLeaseId,
  GetLeaseChargesByLeaseId,
} from '@/graphql/queries/charges'
import { RecurrentCharge, LeaseCharge } from '@/types/charges'

/**
 * Shared helper function to fetch and enrich lease data
 * Reduces duplication between getActiveLease and getLease
 */
const fetchAndEnrichLease = async (
  queryFn: (
    query: DocumentNode,
    variables?: Record<string, unknown>,
  ) => Promise<{ data: unknown }>,
  user: ReturnType<typeof useAuth0>['user'],
  leaseQuery: typeof getActiveLeaseQuery | typeof getLeaseQuery,
  dataPath: 'data.activeLease' | 'data.lease',
): Promise<Lease | null> => {
  if (!user) return null

  const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

  const variables = {
    personId: user[dataAccessKey].person_id,
    doorId: user[dataAccessKey].brands[0]?.rentals[0]?.door_id,
  }

  const { data } = await queryFn(leaseQuery, variables)
  const lease = get(data, dataPath, null) as Lease | null

  const { data: addressData } = await queryFn(
    getAddressByDoorIdQuery,
    variables,
  )
  const { data: moveOutData } = await queryFn(getMoveOutQuery, {
    leaseId: lease?.lease_id,
  })

  const { data: occupantsData } = await queryFn(getLeaseOccupantsQuery, {
    leaseId: lease?.lease_id,
  })

  const occupants = get(
    occupantsData,
    'data.leaseOccupants.occupants',
    [],
  ) as Occupant[]
  const names = occupants?.map(
    (occupant: Occupant) =>
      occupant.person.first_name + ' ' + occupant.person.last_name,
  )
  const personIds = occupants?.map((occupant: Occupant) => occupant.person_id)
  const occupantNames = names?.join(', ')

  const { data: animalPersonMapsData } = await queryFn(
    getAnimalPersonMapsQuery,
    {
      personIds: personIds,
    },
  )
  const animalMaps = get(
    animalPersonMapsData,
    'data.animalPersonMaps.animals',
    [],
  ) as AnimalPersonMap[]
  const animals = animalMaps?.map(
    (animal: AnimalPersonMap) => animal.animal.animal_name,
  )

  const petNames = animals ? [...new Set(animals)].join(', ') : undefined

  const addressInfo = get(
    addressData,
    'data.addressByDoorId',
    null,
  ) as Address | null

  const moveOut = get(moveOutData, 'data.moveOut', []) as MoveOut[]

  const activeMoveOut = moveOut?.find((moveOut: MoveOut) => {
    const isIncluded = moveOut.person_array?.some(
      (person: PersonArray) =>
        person.person_id == user[dataAccessKey].person_id,
    )
    const isInProgress =
      moveOut.move_out_status_id == MoveOutStatusId.IN_PROGRESS
    return isIncluded && isInProgress
  })

  const hasCompletedMoveOut = moveOut?.some((moveOut: MoveOut) => {
    const isIncluded = moveOut.person_array?.some(
      (person: PersonArray) =>
        person.person_id == user[dataAccessKey].person_id,
    )
    const isCompleted = moveOut.move_out_status_id == MoveOutStatusId.COMPLETED
    return isCompleted && isIncluded
  })

  const fullAddress = concatenateAddress(addressInfo)

  return lease
    ? {
        ...lease,
        moveOut: activeMoveOut || null,
        lease_address: fullAddress,
        occupant_names: occupantNames,
        pet_names: petNames,
        hasCompletedMoveOut: hasCompletedMoveOut,
        hasPendingMoveOut: activeMoveOut !== undefined || false,
      }
    : null
}

/**
 * Resident API service
 */
export const useResidentService = () => {
  const { query } = useServiceRequest(AxiosRequestService.RESIDENT)
  const { user } = useAuth0()

  return {
    getActiveLease: async (): Promise<Lease | null> => {
      return fetchAndEnrichLease(
        query,
        user,
        getActiveLeaseQuery,
        'data.activeLease',
      )
    },

    getLease: async (): Promise<Lease | null> => {
      return fetchAndEnrichLease(query, user, getLeaseQuery, 'data.lease')
    },

    getMoveOutReasons: async (): Promise<MoveOutReason[]> => {
      const { data } = await query(getMoveOutReasonsQuery)
      const moveOutReasons = get(
        data,
        'data.moveOutReasons',
        [],
      ) as MoveOutReason[]
      return moveOutReasons
    },

    getMoveOutStatuses: async (): Promise<MoveOutStatus[]> => {
      const { data } = await query(getMoveOutStatusesQuery)
      const moveOutStatuses = get(
        data,
        'data.moveOutStatuses',
        [],
      ) as MoveOutStatus[]
      return moveOutStatuses
    },

    createMoveOut: async (
      moveOutInput: MoveOutInputType,
    ): Promise<MoveOut | null> => {
      const { data: occupantsData } = await query(getLeaseOccupantsQuery, {
        leaseId: moveOutInput.lease_id,
      })

      const occupants = get(
        occupantsData,
        'data.leaseOccupants.occupants',
        [],
      ) as Occupant[]

      const person_array = occupants.map((occupant: Occupant) => ({
        person_id: occupant.person_id,
        agent_id: null,
        active: true,
        ntv_date: moveOutInput.ntv_date,
      }))

      const { data } = await query(createMoveOutQuery, {
        moveOutInput: { ...moveOutInput, person_array },
      })

      return get(data, 'data.createMoveOut', null) as MoveOut | null
    },

    getLeaseCashLedger: async (
      leaseId?: string,
    ): Promise<GetLeaseCashLedgerResponse | null> => {
      if (!user || !leaseId) return null

      const variables = { leaseId }

      const { data } = await query(getLeaseCashLedgerQuery, variables)

      return get(data, 'data.leaseCashLedger', null)
    },

    getResident: async (): Promise<Resident | null> => {
      if (!user) return null

      const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

      const variables = {
        residentId: user[dataAccessKey].person_id,
      }

      const { data } = await query(getResidentQuery, variables)

      return get(data, 'data.resident', null)
    },

    getTermsAndConditionsAcceptance:
      async (): Promise<TermsAndConditionsAcceptance | null> => {
        if (!user) return null

        const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

        const variables = {
          personId: user[dataAccessKey].person_id,
          corporationId: user[dataAccessKey].corporation_id,
        }

        const { data } = await query(
          getTermsAndConditionsAcceptanceQuery,
          variables,
        )

        return get(data, 'data.termsAndConditionsAcceptance', null)
      },

    getActiveTermsOfServiceVersion:
      async (): Promise<TermsOfServiceVersion | null> => {
        const { data } = await query(getActiveTermsOfServiceVersionQuery)

        return get(data, 'data.activeTermsOfServiceVersion', null)
      },

    getActivePrivacyPolicyVersion:
      async (): Promise<PrivacyPolicyVersion | null> => {
        const { data } = await query(getActivePrivacyPolicyVersionQuery)

        return get(data, 'data.activePrivacyPolicyVersion', null)
      },

    acceptTermsAndConditions: async (
      termsOfServiceVersionId: string,
      privacyPolicyVersionId: string,
    ): Promise<AcceptTermsAndConditionsResponse | null> => {
      if (!user) return null

      const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

      const input: AcceptTermsAndConditionsInput = {
        personId: user[dataAccessKey].person_id,
        corporationId: user[dataAccessKey].corporation_id,
        termsOfServiceVersionId,
        privacyPolicyVersionId,
      }

      const { data } = await query(acceptTermsAndConditionsMutation, { input })

      return get(data, 'data.acceptTermsAndConditions', null)
    },

    getLeaseBalance: async (
      leaseId?: string,
    ): Promise<GetLeaseBalanceResponse | null> => {
      if (!user || !leaseId) return null

      const variables = { leaseId }

      const { data } = await query(getLeaseBalanceQuery, variables)

      return get(data, 'data.leaseBalance', null)
    },

    getPrepayment: async (
      leaseId?: string,
    ): Promise<GetPrepaymentResponse | null> => {
      if (!user || !leaseId) return null

      const variables = { leaseId }

      const { data } = await query(getPrepaymentQuery, variables)

      return get(data, 'data.prepayment', null)
    },

    getActiveLeaseSettingByLeaseId: async (
      leaseId?: string,
    ): Promise<LeaseSetting | null> => {
      if (!user || !leaseId) return null

      const variables = { leaseId }

      const { data } = await query(
        getActiveLeaseSettingByLeaseIdQuery,
        variables,
      )

      return get(data, 'data.activeLeaseSettingByLeaseId', null)
    },

    upsertForwardingAddress: async (
      input: Address,
    ): Promise<Address[] | null> => {
      if (!user) return null

      const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

      // Only use current person's ID to update their forwarding address
      const currentPersonId = user[dataAccessKey].person_id

      const { data: upsertForwardingAddressData } = await query(
        upsertForwardingAddressMutation,
        { personIds: [currentPersonId], addressInput: input },
      )

      return get(
        upsertForwardingAddressData,
        'data.upsertForwardingAddress',
        null,
      )
    },

    getAddressByPersonId: async (): Promise<Address | null> => {
      if (!user) return null

      const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

      const variables = {
        personId: user[dataAccessKey].person_id,
        addressTypeId: AddressTypeId.FORWARDING,
      }

      const { data } = await query(getAddressByPersonIdQuery, variables)
      return get(data, 'data.addressByPersonId', null) as Address | null
    },

    getManagementFeePolicies: async (
      policyTypeIds: string[],
    ): Promise<GetManagementFeePoliciesResponse | null> => {
      if (!user) return null

      const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`

      const variables = {
        doorId: user[dataAccessKey].brands[0]?.rentals[0]?.door_id,
        policyTypeIds,
      }

      const { data } = await query(getManagementFeePoliciesQuery, variables)
      return get(
        data,
        'data.managementFeePolicies',
        null,
      ) as GetManagementFeePoliciesResponse | null
    },

    getRecurrentChargesByLeaseId: async (
      leaseId?: string,
    ): Promise<RecurrentCharge[]> => {
      if (!leaseId) return []

      const variables = { leaseId }

      const { data } = await query(GetRecurrentChargesByLeaseId, variables)

      return get(
        data,
        'data.recurrentChargesByLeaseId',
        [],
      ) as RecurrentCharge[]
    },

    getLeaseChargesByLeaseId: async (
      leaseId?: string,
    ): Promise<LeaseCharge[]> => {
      if (!leaseId) return []

      const variables = { leaseId }

      const { data } = await query(GetLeaseChargesByLeaseId, variables)

      return get(data, 'data.leaseChargesByLeaseId', []) as LeaseCharge[]
    },
  }
}
